import React, {useState, useEffect, useCallback} from 'react';
import './_grounds.scss';
import {Loader, Pagination} from 'components';
import {FaRegEye} from 'react-icons/fa';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import {routes} from 'common';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {generalExtraActions} from 'reduxStore/actions';
import {APPROVAL_STATUS, APPROVAL_STATUS_LABEL} from "../../common/constants";

const statusFilterOptions = [
  {label: 'Active', value: 'active'},
  {label: 'Inactive', value: 'inactive'},
];

const approvalFilterOptions = [
  {label: APPROVAL_STATUS_LABEL[APPROVAL_STATUS.PENDING], value: APPROVAL_STATUS.PENDING},
  {label: APPROVAL_STATUS_LABEL[APPROVAL_STATUS.APPROVED], value: APPROVAL_STATUS.APPROVED},
  {label: APPROVAL_STATUS_LABEL[APPROVAL_STATUS.REJECTED], value: APPROVAL_STATUS.REJECTED},
];

const sortOptions = [
  {value: '-created', label: 'Date Added (Newest)'},
  {value: 'created', label: 'Date Added (Oldest)'},
  {value: '-modified', label: 'Date Modified (Newest)'},
  {value: 'modified', label: 'Date Modified (Oldest)'},
];

function Grounds({approvalsMode = false}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {grounds, groundsLoading, groundsError, groundsPagination} = useSelector((state) => state.general);

  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };
    if (sortBy) {
      params.ordering = sortBy;
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') params.is_active = true;
      else if (statusFilter === 'inactive') params.is_active = false;
    }
    if (approvalFilter !== 'all') {
      if (approvalFilter === APPROVAL_STATUS.PENDING) params.approval_status = APPROVAL_STATUS.PENDING;
      else if (approvalFilter === APPROVAL_STATUS.APPROVED) params.approval_status = APPROVAL_STATUS.APPROVED;
      else if (approvalFilter === APPROVAL_STATUS.REJECTED) params.approval_status = APPROVAL_STATUS.REJECTED;
    }
    if (search) {
      params.search = search;
    }
    return params;
  }, [currentPage, itemsPerPage, sortBy, statusFilter, search, approvalFilter, approvalsMode]);

  const handleStatusFilter = (status) => {
    setStatusFilter(prev => (prev === status ? 'all' : status));
  };

  const handleApprovalFilter = (approval) => {
    setApprovalFilter(prev => (prev === approval ? 'all' : approval));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleViewGround = (groundId) => {
    if (approvalsMode) {
      navigate(`/approval/${groundId}`);
    } else {
      navigate(`${routes.GROUNDS}/${groundId}`);
    }
  };

  const handleCreateGround = () => {
    navigate(routes.GROUND_CREATE);
  };

  useEffect(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.getGrounds(params));
  }, [dispatch, buildApiParams]);

  const totalItems = groundsPagination.count;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (groundsError) {
    return (
      <div className="grounds-page custom-listing">
        <div className="error-message">
          <h3>Error loading grounds</h3>
          <p>{groundsError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.getGrounds(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grounds-page custom-listing">
      {groundsLoading && <Loader/>}
      <div className="listing-header">
        <div className="listing-title">{approvalsMode ? 'Approvals:' : 'Grounds:'}</div>
        {!approvalsMode && (
          <button className="listing-create-btn" onClick={handleCreateGround}>Add new Ground</button>
        )}
      </div>
      <div className="listing-filters">
        <div>
          <Filters filters={statusFilterOptions} selectedFilter={statusFilter} handleFilter={handleStatusFilter}/>
          <Filters filters={approvalFilterOptions} selectedFilter={approvalFilter} handleFilter={handleApprovalFilter}/>
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange}/>
          <SearchBar value={search} onChange={handleSearch} placeholder="Search grounds..." debounceDelay={500}
                     onDebouncedChange={handleSearch}/>
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Contact</th>
            <th>Email</th>
            <th>Approval</th>
            <th>Status</th>
            <th>Date Added</th>
            <th>Date Modified</th>
            <th>View</th>
          </tr>
          </thead>
          <tbody>
          {grounds.length === 0 ? (
            <tr>
              <td colSpan="8" style={{textAlign: 'center', color: '#888'}}>No grounds found.</td>
            </tr>
          ) : (
            grounds.map((ground, idx) => (
              <tr key={ground.id}>
                <td>{ground.id}</td>
                <td>{ground.name}</td>
                <td>{ground.contact_number}</td>
                <td>{ground.email}</td>
                <td>
                  <span
                    className={`badge badge-status${ground.approval_status === APPROVAL_STATUS.APPROVED ? '' : ' inactive'}`}
                    data-status={ground.approval_status}
                  >{APPROVAL_STATUS_LABEL[ground.approval_status]}</span>
                </td>
                <td>
                  <span
                    className={`badge badge-status${ground.is_active ? '' : ' inactive'}`}>{ground.is_active ? "Active" : "Inactive"}</span>
                </td>
                <td>{ground.created ? new Date(ground.created).toLocaleDateString() : 'N/A'}</td>
                <td>{ground.modified ? new Date(ground.modified).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <button
                    className="view-btn"
                    onClick={() => handleViewGround(ground.id)}
                    title={approvalsMode ? "Review ground for approval" : "View ground details"}
                  >
                    <FaRegEye/>
                  </button>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
}

export default Grounds;